import React, { useMemo, useState } from "react";
import { shuffle, pick, precisionOf, starsOf, Hud, Flash, FLASH_MS } from "./nqShared";

const MISSIONS = [
  { title: "Atteindre le château", steps: ["🔑 Récupérer la clé", "🗺️ Trouver la carte", "🕳️ Éviter le piège", "🧪 Récupérer la potion", "🏰 Entrer au château"], surprise: { after: 2, step: "🛶 Trouver la barque (le pont est effondré !)" } },
  { title: "Préparer l'expédition", steps: ["🎒 Vider le sac", "📋 Lire la liste", "🍎 Mettre la nourriture", "🔦 Ajouter la lampe", "🔒 Fermer le sac", "🚪 Partir"], surprise: { after: 3, step: "🧥 Prendre le manteau (il commence à pleuvoir !)" } },
  { title: "Faire un gâteau magique", steps: ["🧼 Se laver les mains", "🥣 Sortir le bol", "🥚 Casser les œufs", "🌾 Ajouter la farine", "🔥 Mettre au four", "🎂 Déguster"], surprise: { after: 3, step: "🛒 Aller chercher du sucre (il n'y en a plus !)" } },
  { title: "Rentrer de l'école", steps: ["🏫 Sortir de l'école", "🏠 Rentrer à la maison", "🍪 Prendre le goûter", "📚 Faire les devoirs", "🎮 Jouer"], surprise: { after: 2, step: "🐶 Sortir le chien (il gémit devant la porte !)" } },
];
const CFG = { 1: { rounds: 2, len: 3, surprise: false }, 2: { rounds: 2, len: 5, surprise: false }, 3: { rounds: 2, len: 6, surprise: true } };

export const brief = (level) => {
  const c = CFG[level.n];
  return {
    title: "Trace le chemin avant de partir",
    goal: "remettre les étapes de la mission dans le bon ordre.",
    regarde: "Les étapes sont mélangées. Lis-les toutes avant de toucher quoi que ce soit.",
    fais: "Touche la première étape, puis la deuxième, et ainsi de suite. Ton plan se construit en haut.",
    attention: `Réfléchis avant de toucher : une étape au mauvais moment compte comme une erreur. ${c.rounds} missions de ${c.len} étapes.${c.surprise ? " Un imprévu peut surgir : il faudra l'ajouter au plan au bon moment !" : ""}`,
    preview: { ok: ["1️⃣ 🧼 Se laver les mains", "2️⃣ 🥣 Sortir le bol"], ko: ["🎂 Déguster… en premier !"], okLabel: "Le bon ordre", koLabel: "Le piège" },
  };
};

const makeRound = (cfg, used) => {
  const m = pick(MISSIONS.filter((x) => !used.includes(x.title) && x.steps.length >= cfg.len)) || pick(MISSIONS);
  const steps = m.steps.slice(0, cfg.len);
  return { title: m.title, steps, surprise: cfg.surprise ? m.surprise : null, pool: shuffle(steps) };
};

export default function GameCarte({ power, level, onFinish, practice, onPracticeEnd }) {
  const cfg = useMemo(() => (practice ? { rounds: 1, len: 3, surprise: false } : CFG[level.n]), [level, practice]);
  const [round, setRound] = useState(() => makeRound(cfg, []));
  const [n, setN] = useState(0);
  const [plan, setPlan] = useState([]);
  const [stats, setStats] = useState({ ok: 0, ko: 0 });
  const [shake, setShake] = useState(null);
  const [flash, setFlash] = useState(null);
  const [surprised, setSurprised] = useState(false);
  const [notice, setNotice] = useState(null);
  const expectedList = useMemo(() => {
    if (!round.surprise || !surprised) return round.steps;
    const arr = [...round.steps]; arr.splice(round.surprise.after, 0, round.surprise.step); return arr;
  }, [round, surprised]);
  const expected = expectedList[plan.length];
  const pop = (k) => { setFlash(k); setTimeout(() => setFlash(null), FLASH_MS); };

  const tap = (step) => {
    if (notice) return;
    if (step === expected) {
      const nextPlan = [...plan, step];
      setPlan(nextPlan); setStats((s) => ({ ...s, ok: s.ok + 1 })); pop("ok");
      if (round.surprise && !surprised && nextPlan.length === round.surprise.after) {
        setSurprised(true); setNotice(round.surprise.step); setTimeout(() => setNotice(null), 2500);
        return;
      }
      if (nextPlan.length === expectedList.length) {
        if (n + 1 >= cfg.rounds) {
          if (practice) { setTimeout(onPracticeEnd, 600); return; }
          const total = stats.ok + 1 + stats.ko; const hits = stats.ok + 1;
          const precision = precisionOf(hits, total, 0);
          setTimeout(() => onFinish({ hits, total, errors: stats.ko, precision, stars: starsOf(precision), detail: `${cfg.rounds} plan(s) tracé(s) · ${stats.ko} étape(s) placée(s) au mauvais moment` }), 600);
        } else {
          setTimeout(() => { setRound(makeRound(cfg, [round.title])); setPlan([]); setSurprised(false); setN(n + 1); }, 900);
        }
      }
    } else { setStats((s) => ({ ...s, ko: s.ko + 1 })); setShake(step); pop("ko"); setTimeout(() => setShake(null), 400); }
  };

  const remaining = [...round.pool, ...(surprised ? [round.surprise.step] : [])].filter((s) => !plan.includes(s));
  return (
    <div className="space-y-4" data-testid="nq-game-carte">
      <Hud items={[["Mission", `${n + 1}/${cfg.rounds}`, power.color], ["Étapes placées", `${plan.length}/${expectedList.length}`, "#7CC6A6"], ["Erreurs", stats.ko, "#E0705C"]]} />
      <div className="rounded-3xl bg-[#E3EEF9] p-5 space-y-4 relative">
        <Flash kind={flash} koLabel="✗ Pas maintenant !" />
        {notice && <div className="absolute inset-0 z-10 rounded-3xl bg-[#FFF3D6]/95 flex flex-col items-center justify-center text-center p-6 animate-fade-up" data-testid="nq-carte-surprise"><div className="text-5xl mb-2">⚠️</div><div className="font-serif-title text-2xl text-[#B8781A]">Imprévu !</div><p className="text-[var(--ink)]">Nouvelle étape à placer maintenant : <strong>{notice}</strong></p></div>}
        <div className="text-center"><div className="text-xs uppercase tracking-[0.25em] font-bold text-[var(--ink-soft)]">Mission</div><div className="font-serif-title text-2xl text-[var(--ink)]" data-testid="nq-carte-title">{round.title}</div><div className="text-sm text-[var(--ink)] mt-1">{plan.length === 0 ? "Quelle est la toute première étape ?" : "Et ensuite ?"}</div></div>
        <div className="rounded-2xl bg-white p-3 min-h-[3.5rem] flex flex-wrap gap-2 items-center" data-testid="nq-carte-plan">
          {plan.length === 0 && <span className="text-sm text-[var(--ink-soft)] italic">🗺️ Mon plan apparaîtra ici…</span>}
          {plan.map((s, i) => <span key={s} className="animate-fade-up inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full text-white font-medium" style={{ background: power.color }}><span className="w-5 h-5 rounded-full bg-white/30 text-[10px] flex items-center justify-center font-bold">{i + 1}</span>{s}</span>)}
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {remaining.map((s, i) => <button key={s} onClick={() => tap(s)} data-testid={`nq-carte-step-${i}`} className={`rounded-2xl bg-white border-2 border-[var(--border)] px-4 py-3.5 text-left text-base font-medium text-[var(--ink)] hover:border-[#5E9BD6] active:scale-95 transition-all ${shake === s ? "nq-shake ring-4 ring-[#E0705C]" : ""}`}>{s}</button>)}
        </div>
      </div>
    </div>
  );
}
